/**
 * ItemGo Admin Portal — Entry Point
 */
import './index.css';
import { initErrorLogger, registry } from '@itemgo/core';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { initializeFirebase, auth, db } from '@itemgo/core';
import { AuthProvider } from '@itemgo/core';
import { initPlugins } from './lib/initPlugins';
import { ErrorBoundary } from '@itemgo/ui';
import { requestNotificationPermission, initForegroundMessaging } from '@itemgo/messaging';
import { doc, setDoc } from 'firebase/firestore';
import App from './App';

// Initialize error logger FIRST — before Firebase, React, anything
initErrorLogger();
initializeFirebase();
initPlugins();

// Connect registry to Firestore so plugin states persist across sessions
registry.connect(db);
registry.loadStates().catch(() => {});

// Register FCM token after auth state is ready
auth.onAuthStateChanged((user) => {
  if (user) {
    requestNotificationPermission(user.uid).catch(() => {});
  }
});

// ─── Native Capacitor FCM token listener ─────────────────────────
window.addEventListener("itemgo:nativeFcmToken", (event: any) => {
  saveNativeFcmToken(event?.detail);
});

try {
  const stored = localStorage.getItem("itemgo_native_fcm_token");
  if (stored) {
    const [token, ts] = stored.split("|");
    if (token && Date.now() - parseInt(ts) < 3600000) {
      saveNativeFcmToken(token);
      localStorage.removeItem("itemgo_native_fcm_token");
    }
  }
} catch {}

// Read native FCM token passed via URL param from the launcher
try {
  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get("fcm_token");
  if (urlToken) {
    (window as any).__pendingNativeFcmToken = urlToken;
    trySavePendingToken();
  }
} catch {}

const authUnsub2 = auth.onAuthStateChanged(() => {
  trySavePendingToken();
});
setTimeout(() => authUnsub2(), 10000);

function trySavePendingToken() {
  const pending = (window as any).__pendingNativeFcmToken;
  const user = auth.currentUser;
  if (pending && user?.uid) {
    saveNativeFcmToken(pending);
    delete (window as any).__pendingNativeFcmToken;
  }
}

function saveNativeFcmToken(token: string) {
  const user = auth.currentUser;
  if (!token || !user?.uid) return;
  setDoc(
    doc(db, "users", user.uid, "fcmTokens", token),
    { token, platform: "native", createdAt: new Date() },
    { merge: true }
  ).catch(() => {});
}

// Initialize foreground message handler
initForegroundMessaging('/dashboard');

createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </ErrorBoundary>
);
